import {
  Activity,
  ArrowRightLeft,
  AppWindow,
  BadgeCheck,
  Bell,
  BookOpen,
  Bot,
  Boxes,
  Cpu,
  Database,
  Gauge,
  Inbox,
  Key,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ShieldCheck,
  Sparkles,
  Table2,
  Megaphone,
  MessageSquare,
  Network,
  PlayCircle,
  Printer,
  RadioTower,
  ScrollText,
  Send,
  Settings2,
  SlidersHorizontal,
  Tags,
  TerminalSquare,
  UserCheck,
  UserCog,
  Users,
  UsersRound,
  Wallet,
  Waypoints,
  Workflow as WorkflowIcon,
  Plug,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { appUsersApi } from "@/lib/api-app-users";
import { cn } from "@/lib/cn";
import { meApi } from "@/lib/api-me";
import { usePermissions } from "@/lib/permissions";

/**
 * Sidebar = a thin ACTIVITY RAIL + a section PANEL (ADR-0026):
 *
 *   ┌──┬──────────────────────┐
 *   │▢ │  Data                │   ▢ brand
 *   │⌂ │  ┌ Modelli           │   ⌂ home (Dashboard) — standalone link
 *   │▰ │  │   Canonici        │   ▰ Data section  ┐ middle rail icons
 *   │▱ │  │   Workflow        │   ▱ Apps section  ┘ (one per plane)
 *   │  │  └ Flussi …          │   ⚙ Admin — pinned to the rail bottom
 *   │⚙ │  v0.1.0              │
 *   └──┴──────────────────────┘
 *
 * Rationale (sidebar-IA discussion): the planes are genuinely different
 * mental contexts (move data between systems vs. build internal apps vs.
 * operator admin). The rail keeps every plane one click away WITHOUT a
 * segmented pill that doesn't scale — adding the Odino Ask later is
 * just one more rail icon. The panel shows ONLY the active section's
 * grouped items, so each world stays focused. Dashboard is a first-class
 * rail icon (home), never an orphaned list row.
 *
 * The active section auto-follows the route (open an app → Apps panel) and
 * is persisted to localStorage, so manual browsing and deep-links both land
 * coherently. Cross-section jumps happen via the rail or the ⌘K palette.
 *
 * A CHILDLESS rail entry (Dashboard/home) shows no panel — the aside shrinks
 * to the rail and the content reclaims the width. On desktop the sidebar also
 * collapses to the rail alone (~56px); in that mode hovering a section icon
 * pops a FLYOUT with its submenu (the panel is hidden, so the flyout keeps
 * every group reachable), and a click jumps to the section's first page. The
 * collapse state lives in :class:`AppShell`, toggled from the Topbar hamburger.
 *
 * The whole aside is nudged down by ``md:pt-2`` to match the lifted main
 * panel's ``md:mt-2`` (AppShell), so the brand mark + panel header sit on the
 * same baseline as the topbar breadcrumb.
 */

/** A rail SECTION — each owns a panel of grouped nav items (ADR-0026). `data` is
 *  the catalog + semantic model (L0-L2); `flows` is the integration engine
 *  (canonical contracts + workflows + observe + alerts). */
type Section = "data" | "flows" | "apps" | "ai" | "admin";

/** Where an item sits: inside a rail section's panel, or the standalone
 *  ``home`` link (Dashboard) pinned at the top of the rail. */
type Slot = Section | "home";

/**
 * Each item carries a translation key (``labelKey``), a ``slot`` (which rail
 * section's panel it belongs to, or the standalone ``home`` link) and an
 * optional ``groupKey``. Rendering reads the i18n keys via i18next so the
 * sidebar switches language live.
 */
const NAV_ITEMS: Array<{
  to: string;
  labelKey: string;
  icon: LucideIcon;
  slot: Slot;
  groupKey?: string;
  /** Optional live count chip on the item (e.g. pending registrations). */
  badge?: "pendingRegistrations";
  /** RBAC resource gating this page's backend routes. The item is shown
   *  only when the caller holds ANY grant on it (lib/permissions.ts) —
   *  a UX courtesy; the server gates stay authoritative. Omit for
   *  pages every authenticated operator may open (e.g. Docs). */
  resource?: string;
}> = [
  // ── HOME — the global overview. A standalone link pinned at the top of the
  //    rail (its own page, no panel of sub-items), so it's never an orphan.
  {
    to: "/dashboard",
    resource: "dashboard",
    labelKey: "sidebar.dashboard",
    icon: LayoutDashboard,
    slot: "home",
  },

  // ── DATA plane (ADR-0026) — the company data fabric. Today: the
  //    integration engine (Flussi) + its contracts (Modelli) + observability
  //    + alerting. The federated **Sorgenti** + **Dataset** catalog and the
  //    **Semantica** layer land here as their own groups when those features
  //    ship (so no nav points at routes that don't exist yet).
  // Sorgenti (L0) — the catalog of registered connections (internal Tray +
  // Catalogo (L0 Sorgenti + L1 Dataset) — what data exists + its publication.
  // One group, mirroring the ADR-0026 layers (a Dataset is a surface over a
  // Source, so they belong together).
  {
    to: "/sources",
    resource: "sources",
    labelKey: "sidebar.sources",
    icon: Database,
    slot: "data",
    groupKey: "sidebar.groups.catalog",
  },
  {
    to: "/datasets",
    resource: "datasets",
    labelKey: "sidebar.datasets",
    icon: Table2,
    slot: "data",
  },
  {
    to: "/knowledge-bases",
    resource: "knowledge_bases",
    labelKey: "sidebar.knowledgeBases",
    icon: BookOpen,
    slot: "data",
  },
  // Modello semantico (L2) — what the data MEANS + how it's related. Semantica
  // (per-field meaning, PII/sensitivity coverage) + the relationships graph.
  {
    to: "/datasets/semantics",
    resource: "semantics",
    labelKey: "sidebar.semantics",
    icon: Tags,
    slot: "data",
    groupKey: "sidebar.groups.semantic",
  },
  {
    to: "/datasets/graph",
    resource: "semantics",
    labelKey: "sidebar.dataGraph",
    icon: Network,
    slot: "data",
  },

  // ── INTEGRAZIONE plane — the movement/contract ENGINE, split out of the data
  //    catalog (two different headspaces): canonical contracts + flows + observe
  //    + alerts. Canonical models (L3) live here because in practice they're the
  //    integration lingua franca workflows map to, not part of the catalog.
  {
    to: "/canonical",
    resource: "canonical",
    labelKey: "sidebar.canonical",
    icon: Boxes,
    slot: "flows",
    groupKey: "sidebar.groups.models",
  },
  // Flussi — the two ENGINES of the integration plane: the workflow engine
  // (canonical pipelines + orchestration) and the ETL engine. Their supporting
  // config (mapping/ingress/rate-limit) lives in the "Configurazione" group
  // below; ETL lanes are reached from inside the ETL pipelines page (minimal
  // corsie UI), not the nav.
  {
    to: "/workflows",
    resource: "workflows",
    labelKey: "sidebar.workflows",
    icon: WorkflowIcon,
    slot: "flows",
    groupKey: "sidebar.groups.flows",
  },
  { to: "/etl", resource: "etl", labelKey: "sidebar.etl", icon: Waypoints, slot: "flows" },
  // Configurazione — supporting reference data + entry points + throttling that
  // the engines consume (not flows themselves).
  {
    to: "/mappings",
    resource: "mappings",
    labelKey: "sidebar.mappings",
    icon: ArrowRightLeft,
    slot: "flows",
    groupKey: "sidebar.groups.config",
  },
  { to: "/ingress", resource: "ingress", labelKey: "sidebar.ingress", icon: Inbox, slot: "flows" },
  {
    to: "/rate-limits",
    resource: "rate_limits",
    labelKey: "sidebar.rateLimits",
    icon: Gauge,
    slot: "flows",
  },
  // Osserva — "what just happened?": the live map + the flow trail (inbound
  // messages → processing runs → outbound deliveries) + activity + internals.
  {
    to: "/topology",
    resource: "topology",
    labelKey: "sidebar.topology",
    icon: Network,
    slot: "flows",
    groupKey: "sidebar.groups.observe",
  },
  {
    to: "/activity",
    resource: "notifications",
    labelKey: "sidebar.activity",
    icon: Activity,
    slot: "flows",
  },
  {
    to: "/messages",
    resource: "messages",
    labelKey: "sidebar.messages",
    icon: MessageSquare,
    slot: "flows",
  },
  {
    to: "/workflow-runs",
    resource: "executions",
    labelKey: "sidebar.workflowRuns",
    icon: PlayCircle,
    slot: "flows",
  },
  {
    to: "/deliveries",
    resource: "outbound",
    labelKey: "sidebar.deliveries",
    icon: Send,
    slot: "flows",
  },
  {
    to: "/aggregate-buckets",
    resource: "workflows",
    labelKey: "sidebar.aggregateBuckets",
    icon: Layers,
    slot: "flows",
  },
  // Allerte — deterministic alert rules + their channels (configuring one
  // without the other is rare).
  {
    to: "/alerts",
    resource: "alerts",
    labelKey: "sidebar.alerts",
    icon: Bell,
    slot: "flows",
    groupKey: "sidebar.groups.alerts",
  },
  {
    to: "/channels",
    resource: "channels",
    labelKey: "sidebar.channels",
    icon: Megaphone,
    slot: "flows",
  },

  // ── APPS plane — the low-code application plane (ADR-0010). Each app
  //    owns its own internal nav (tables / forms / pages / members) INSIDE
  //    the app, so the global Apps plane is intentionally lean: the app
  //    list + the portals' END-USERS (`app_users` — a realm distinct from
  //    the operators in Admin, hence here next to the apps they belong to).
  { to: "/apps", resource: "apps", labelKey: "sidebar.apps", icon: AppWindow, slot: "apps" },
  // Reports (ADR-0060) — tenant-global Report Types (+ designer, next slices),
  // optionally linked to apps (the ADR-0059 pattern).
  {
    to: "/report-types",
    resource: "reports",
    labelKey: "sidebar.reports",
    icon: Printer,
    slot: "apps",
  },
  // Portale — the end-user surface around the apps: portal users (`app_users`),
  // the tenant brand/launcher, security policy, and the registrations queue.
  {
    to: "/app-users",
    resource: "apps",
    labelKey: "sidebar.appUsers",
    icon: UserCog,
    slot: "apps",
    groupKey: "sidebar.groups.portal",
  },
  // Parent portal (ADR-0015) — the tenant brand + launcher/switcher that
  // federate all the apps; its natural home is the Apps plane.
  {
    to: "/parent-portal",
    resource: "parent_portal",
    labelKey: "sidebar.parentPortal",
    icon: LayoutGrid,
    slot: "apps",
  },
  // Portal Security (ADR-0016) — registration policy + 2FA + app access.
  {
    to: "/portal-security",
    resource: "parent_portal",
    labelKey: "sidebar.portalSecurity",
    icon: ShieldCheck,
    slot: "apps",
  },
  // Registrations queue (ADR-0016) — pending self-signups worklist + live count.
  {
    to: "/portal-registrations",
    resource: "apps",
    labelKey: "sidebar.portalRegistrations",
    icon: UserCheck,
    slot: "apps",
    badge: "pendingRegistrations",
  },

  // ── AI plane (ADR-0026 + ADR-0030) — the macro-area for every AI feature,
  //    grouped by intent, MIRRORING the flows plane: the primary objects get
  //    their own group and the parts they consume sit under Configurazione.
  //    Assistenti (Agenti · Squadre) are this plane's ENGINES — they're what an
  //    app's chat/voice interface binds to (the picker offers an agent OR a team,
  //    never a tool) — exactly as Workflow/ETL are the engines of `flows`. LLM
  //    configs / tools / MCP servers are INGREDIENTS an agent composes: nothing
  //    binds an interface to a tool. They all used to sit under one flat
  //    "Configurazione", which weighed `Agenti` the same as `Server MCP`.
  //    The old "Chiedi" (Ask) surface was RETIRED (ADR-0071 — data Q&A lives on
  //    the app chat/voice interfaces; Odino stays rail-only, from the Topbar).
  // NB only the FIRST item of a group carries `groupKey` (the render tracks
  // `lastGroup`), so the order below IS the grouping.
  { to: "/ai/agents", resource: "ai", labelKey: "sidebar.agents", icon: Bot, slot: "ai",
    groupKey: "sidebar.groups.assistant" },
  { to: "/ai/teams", resource: "teams", labelKey: "sidebar.teams", icon: Waypoints, slot: "ai" },
  // Configurazione — what an agent composes (ADR-0033), in build-up order.
  {
    to: "/ai/llm-configs",
    resource: "ai",
    labelKey: "sidebar.llmConfigs",
    icon: Cpu,
    slot: "ai",
    groupKey: "sidebar.groups.config",
  },
  { to: "/ai/tools", resource: "tools", labelKey: "sidebar.tools", icon: Wrench, slot: "ai" },
  {
    to: "/ai/mcp-servers",
    resource: "mcp",
    labelKey: "sidebar.mcpServers",
    icon: Plug,
    slot: "ai",
  },
  // Osserva — spend overview + the run/session/sandbox logs.
  {
    to: "/ai/costs",
    resource: "odino",
    labelKey: "sidebar.aiCosts",
    icon: Wallet,
    slot: "ai",
    groupKey: "sidebar.groups.observe",
  },
  {
    to: "/ai/executions",
    resource: "odino",
    labelKey: "sidebar.executions",
    icon: Activity,
    slot: "ai",
  },
  {
    to: "/ai/sandbox",
    resource: "odino",
    labelKey: "sidebar.sandbox",
    icon: TerminalSquare,
    slot: "ai",
  },

  // ── ADMIN section — operator/management concerns, plane-agnostic. Its own
  //    rail icon (⚙, at the bottom) → its own panel. Users + Groups are the
  //    management-plane RBAC; Credentials + Audit are cross-cutting (used by
  //    webhooks, channels AND data sources, ADR-0026); Docs is the help hub.
  //    No leading group header — the panel title ("Amministrazione") IS it.
  { to: "/users", resource: "users", labelKey: "sidebar.users", icon: Users, slot: "admin" },
  {
    to: "/groups",
    resource: "users",
    labelKey: "sidebar.groupsNav",
    icon: UsersRound,
    slot: "admin",
  },
  {
    to: "/credentials",
    resource: "credentials",
    labelKey: "sidebar.credentials",
    icon: Key,
    slot: "admin",
  },
  {
    to: "/service-accounts",
    resource: "service_accounts",
    labelKey: "sidebar.serviceAccounts",
    icon: BadgeCheck,
    slot: "admin",
  },
  {
    to: "/gateways",
    resource: "gateways",
    labelKey: "sidebar.gateways",
    icon: RadioTower,
    slot: "admin",
  },
  {
    to: "/audit",
    resource: "audit",
    labelKey: "sidebar.auditLog",
    icon: ScrollText,
    slot: "admin",
  },
  {
    to: "/settings",
    resource: "settings",
    labelKey: "sidebar.settings",
    icon: SlidersHorizontal,
    slot: "admin",
  },
  {
    to: "/docs",
    labelKey: "sidebar.docs",
    icon: BookOpen,
    slot: "admin",
    groupKey: "sidebar.groups.help",
  },
];

const SECTION_STORAGE_KEY = "tray:sidebar:plane";

/**
 * Map a pathname to the rail section it belongs to, or ``null`` for the
 * standalone home (Dashboard — keep the current section). Drives both the
 * initial section and the auto-follow on navigation, so the visible panel
 * always matches the page you're on (deep-links + ⌘K jumps included).
 */
function sectionForPath(pathname: string): Section | null {
  // /apps/:id (table designer, page builder, …) live under the apps
  // list item's prefix — keep this explicit so deep app routes resolve.
  if (pathname.startsWith("/apps")) return "apps";
  // Otherwise match any section-scoped nav item by its path, so every page
  // (data / apps / admin) auto-switches the visible panel on navigation.
  for (const item of NAV_ITEMS) {
    if (item.slot !== "home" && pathname.startsWith(item.to)) {
      return item.slot;
    }
  }
  return null;
}

function readStoredSection(): Section | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(SECTION_STORAGE_KEY);
  return v === "apps" || v === "data" || v === "flows" || v === "ai" || v === "admin" ? v : null;
}

/**
 * The rail sections — one icon each on the activity rail (ADR-0026). `data`
 * and `apps` sit in the middle; `admin` is pinned to the rail bottom. Adding a
 * plane (e.g. the Odino Ask) = one more entry here, no layout change — the
 * reason we left the 2-segment pill behind.
 */
const RAIL_SECTIONS: ReadonlyArray<{ key: Section; labelKey: string; icon: LucideIcon }> = [
  { key: "data", labelKey: "sidebar.planes.data", icon: Database },
  { key: "flows", labelKey: "sidebar.planes.flows", icon: WorkflowIcon },
  { key: "apps", labelKey: "sidebar.planes.apps", icon: AppWindow },
  { key: "ai", labelKey: "sidebar.planes.ai", icon: Sparkles },
  { key: "admin", labelKey: "sidebar.planes.admin", icon: Settings2 },
];

/**
 * Map a pathname to the active RAIL ENTRY — including the standalone ``home``
 * (Dashboard). Returns null only for routes we don't recognise (keep current).
 */
function railForPath(pathname: string): Slot | null {
  if (pathname.startsWith("/dashboard")) return "home";
  return sectionForPath(pathname);
}

/** Does a rail entry own a panel of VISIBLE sub-items? ``home`` (Dashboard)
 *  does not — it's a single page, so its panel collapses away (rail-only
 *  width). ``isVisible`` is the permission filter so the aside width and the
 *  rendered panel never disagree. */
function hasPanelItems(
  active: Slot,
  isVisible: (i: (typeof NAV_ITEMS)[number]) => boolean,
): boolean {
  return active !== "home" && NAV_ITEMS.some((i) => i.slot === active && isVisible(i));
}

/**
 * The first nav destination the caller can actually open. Used to land a
 * low-privilege operator somewhere usable instead of dumping them on the
 * permission-gated Dashboard (its data 403s without `dashboard:read`).
 * Falls back to the always-open profile so it never returns nothing.
 */
export function firstAccessiblePath(canSee: (resource: string) => boolean): string {
  const first = NAV_ITEMS.find((i) => !i.resource || canSee(i.resource));
  return first?.to ?? "/account";
}

/**
 * The active rail entry. Route wins (deep-links / ⌘K land coherently), then the
 * last manual section pick, then a sensible default. Shared by the <aside>
 * (which sizes itself on whether the active entry HAS a panel) and SidebarNav
 * (which renders it), so the chrome and the width never disagree.
 */
function useRailSection(): readonly [Slot, (s: Slot) => void] {
  const location = useLocation();
  const [active, setActive] = useState<Slot>(
    () => railForPath(location.pathname) ?? readStoredSection() ?? "data",
  );
  useEffect(() => {
    const r = railForPath(location.pathname);
    if (r && r !== active) setActive(r);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only persist real sections — ``home`` is route-derived, not a sticky pick.
    if (typeof window !== "undefined" && active !== "home") {
      window.localStorage.setItem(SECTION_STORAGE_KEY, active);
    }
  }, [active]);
  return [active, setActive] as const;
}

/**
 * Inner content of the sidebar — header, plane switcher, grouped nav,
 * version footer. Rendered both inside the desktop <aside> and inside
 * the mobile Sheet. `onNavigate` is called whenever a nav link is
 * clicked, used by the mobile drawer to close itself. `collapsed=true`
 * hides labels (desktop icon-only mode); the mobile drawer always
 * renders expanded.
 */
export function SidebarNav({
  onNavigate,
  collapsed = false,
  active: activeProp,
  setActive: setActiveProp,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
  /** Active rail entry — passed down by the desktop <aside> so it can size
   *  itself. Omitted in the mobile Sheet, where we manage our own. */
  active?: Slot;
  setActive?: (s: Slot) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Desktop: active/setActive arrive as props (so the <aside> and the chrome
  // agree on the width). Mobile Sheet: no props → fall back to our own hook.
  const own = useRailSection();
  const active = activeProp ?? own[0];
  const setActive = setActiveProp ?? own[1];

  // Permission-aware nav: items whose RBAC resource the caller holds no
  // grant on are hidden (UX courtesy — backend routes stay gated). While
  // `/me` loads, everything shows (fail-open, no flash of empty nav).
  const { canSee } = usePermissions();
  const visibleItems = NAV_ITEMS.filter((i) => !i.resource || canSee(i.resource));

  // The running image's version, shown as a grey label at the bottom of the
  // Amministrazione panel (under Help). Shares the `["me"]` query cache with
  // usePermissions (no extra fetch); "dev" when un-tagged / still loading.
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => meApi.get(), staleTime: 5 * 60_000 });
  const appVersion = meQ.data?.app_version;

  // Live count for the pending-registrations badge. One cheap query (we only
  // read `total`) polled in the background; it drives the chip on the
  // Registrations item so an operator sees a backlog without opening the page.
  // Skipped entirely when the caller can't see the apps plane (it would 403).
  const pendingRegQ = useQuery({
    queryKey: ["app-users", "pending", "count"],
    queryFn: () => appUsersApi.list({ state: "pending", page_size: 25 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled: canSee("apps"),
  });
  const badgeCounts: Record<NonNullable<(typeof NAV_ITEMS)[number]["badge"]>, number> = {
    pendingRegistrations: pendingRegQ.data?.total ?? 0,
  };

  /**
   * Render a band of items, emitting a group header whenever the
   * ``groupKey`` changes. Each band tracks its own ``lastGroup`` so the
   * first group in a band always shows its header.
   */
  const renderBand = (items: typeof NAV_ITEMS) => {
    let lastGroup: string | undefined;
    return items.map((item, idx) => {
      const showGroup = item.groupKey && item.groupKey !== lastGroup;
      if (item.groupKey) lastGroup = item.groupKey;
      const label = t(item.labelKey);
      const count = item.badge ? badgeCounts[item.badge] : 0;
      return (
        <div key={item.to}>
          {showGroup && (
            // Quiet group header: small, normal-case, light weight. The
            // hierarchy comes from spacing, not typographic shouting. The first
            // header sits tight (the nav's own top padding already gives the gap
            // below the separator); later headers get a fuller gap to separate
            // consecutive groups.
            <div
              className={cn(
                "px-4 pb-1 text-xs font-medium text-muted-foreground/70",
                idx === 0 ? "pt-0.5" : "pt-4",
              )}
            >
              {t(item.groupKey!)}
            </div>
          )}
          <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) => cn("nav-item relative", isActive && "nav-item-active")}
          >
            <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
            <span>{label}</span>
            {count > 0 && (
              <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--warning))]/15 px-1.5 text-[11px] font-medium tabular-nums text-[hsl(var(--warning))]">
                {count}
              </span>
            )}
          </NavLink>
        </div>
      );
    });
  };

  // The active entry's panel items + its header chrome. ``home`` (Dashboard)
  // has no items → no panel (the content takes the full width).
  const panelItems = visibleItems.filter((i) => i.slot === active);
  const activeSection = RAIL_SECTIONS.find((s) => s.key === active);
  const appsBadge = badgeCounts.pendingRegistrations > 0;
  // `home` (Dashboard) is a single page with no sub-items — never open a
  // panel for it (matches the desktop <aside> sizing via hasPanelItems).
  // Without the `active !== "home"` guard the mobile Sheet rendered an
  // empty sub-panel when the Dashboard rail entry was active.
  const showPanel = !collapsed && active !== "home" && panelItems.length > 0;

  /**
   * One rail icon = one section. When collapsed (rail-only) it grows a hover
   * FLYOUT carrying the section's submenu — the panel is hidden in that mode,
   * so this keeps every group one hover away. A click navigates to the
   * section's first page (collapsed) or just opens its panel (expanded).
   */
  const railIcon = (s: (typeof RAIL_SECTIONS)[number], dot = false, dropUp = false) => {
    const on = active === s.key;
    const items = visibleItems.filter((i) => i.slot === s.key);
    return (
      <div key={s.key} className="group/flyout relative">
        <button
          type="button"
          aria-label={t(s.labelKey)}
          aria-pressed={on}
          title={collapsed ? undefined : t(s.labelKey)}
          onClick={() => {
            setActive(s.key);
            if (collapsed) {
              const first = items[0];
              if (first) navigate(first.to);
            }
          }}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-lg transition",
            on
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
        >
          {on && (
            <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-[hsl(var(--accent))]" />
          )}
          <s.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
          {dot && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[hsl(var(--warning))]" />
          )}
        </button>
        {/* Collapsed flyout. ``hidden`` (not ``invisible``) so it takes NO layout
            when idle — an invisible flyout still reserves its box and, for the
            bottom-pinned admin icon, would push the page scrollable. ``pl-1.5``
            (padding, not margin) keeps the 4px gap inside the hoverable area so
            the popover doesn't flicker as the cursor crosses to the card.
            ``dropUp`` anchors the admin flyout to the icon's BOTTOM so it grows
            upward and isn't clipped at the viewport's bottom edge. */}
        {collapsed && (
          <div
            className={cn(
              "absolute left-full z-50 hidden w-56 pl-1.5 group-hover/flyout:block",
              dropUp ? "bottom-0" : "top-0",
            )}
          >
            <div className="max-h-[calc(100vh-1rem)] overflow-y-auto rounded-lg border border-border bg-background py-2 shadow-lg">
              <div
                className="px-4 pb-1.5 text-xs font-semibold text-foreground"
                style={{ fontFamily: "Inter, system-ui, sans-serif" }}
              >
                {t(s.labelKey)}
              </div>
              {renderBand(items)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    // ``md:pt-2`` matches the lifted main panel's ``md:mt-2`` (AppShell) so the
    // brand mark + panel header line up with the topbar breadcrumb. The mobile
    // Sheet (<md) gets no offset.
    <div className="flex h-full w-full bg-background md:pt-2">
      {/* ACTIVITY RAIL — brand · home · sections (data/apps) · admin (bottom).
          Always present; adding a plane (Odino) is just one more icon. */}
      <div
        className={cn(
          "flex w-14 shrink-0 flex-col items-center gap-1 pb-2",
          showPanel && "border-r border-border/50",
        )}
      >
        {/* Brand mark. Path is absolute-from-root because Vite's
            ``base: "/"`` does NOT rewrite paths inside .tsx. */}
        <div className="flex h-topbar w-full items-center justify-center">
          <img
            src="/tray-logo.svg"
            alt=""
            aria-hidden="true"
            width={28}
            height={28}
            className="h-7 w-7"
          />
        </div>
        {/* Home (Dashboard) — a standalone link, never an orphan item. */}
        {canSee("dashboard") && (
          <NavLink
            to="/dashboard"
            onClick={onNavigate}
            aria-label={t("sidebar.dashboard")}
            title={t("sidebar.dashboard")}
            className={({ isActive }) =>
              cn(
                "flex h-10 w-10 items-center justify-center rounded-lg transition",
                isActive
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
              )
            }
          >
            <LayoutDashboard className="h-[18px] w-[18px]" strokeWidth={1.5} />
          </NavLink>
        )}
        <div className="my-1 h-px w-6 bg-border/60" />
        {/* A section whose every item is permission-hidden loses its rail
            icon too — an empty panel would be a dead end. */}
        {RAIL_SECTIONS.filter(
          (s) => s.key !== "admin" && visibleItems.some((i) => i.slot === s.key),
        ).map((s) => railIcon(s, s.key === "apps" && appsBadge))}
        <div className="mt-auto" />
        {visibleItems.some((i) => i.slot === "admin") &&
          railIcon(RAIL_SECTIONS.find((s) => s.key === "admin")!, false, true)}
      </div>

      {/* PANEL — the active section's title + grouped items. Absent when
          collapsed (rail-only) or when the active entry has no sub-items
          (e.g. Dashboard), so the content reclaims the width. */}
      {showPanel && activeSection && (
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            className="flex h-topbar items-center gap-2 px-4 text-sm font-semibold tracking-tight"
            style={{ fontFamily: "Inter, system-ui, sans-serif" }}
          >
            <activeSection.icon
              className="h-4 w-4 shrink-0 text-muted-foreground"
              strokeWidth={1.5}
            />
            {t(activeSection.labelKey)}
          </div>
          {/* Elegant separator between the section title and its items — a
              hairline that fades into the background at both ends, so it reads
              as a soft divider rather than a hard rule. */}
          <div className="mx-3 h-px shrink-0 bg-gradient-to-r from-transparent via-border to-transparent" />
          <nav className="flex-1 overflow-y-auto pt-2 pb-2">{renderBand(panelItems)}</nav>
          {/* Version label — only in Amministrazione (under Help), grey. Reflects the
              running image (= the deployed release tag), via /me's app_version. */}
          {activeSection.key === "admin" && (
            <div className="flex items-center px-4 py-2">
              <span className="text-xs text-muted-foreground">Tray {appVersion ?? "dev"}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Desktop sidebar. Width swaps between the full `w-sidebar` constant
 * (declared in Tailwind config) and a narrow rail when collapsed.
 * Width transitions with a tiny CSS animation so the swap feels
 * intentional rather than abrupt.
 *
 * The toggle lives in the Topbar (hamburger) so collapse/expand is
 * driven from a single, predictable affordance regardless of viewport.
 */
export function Sidebar({ collapsed }: { collapsed: boolean }) {
  // The active rail entry is owned here so the <aside> can size itself: full
  // ``w-sidebar`` when a panel shows, the bare rail (``w-14``) when collapsed
  // OR when the active entry has no sub-items (Dashboard, or a section whose
  // every item is permission-hidden). Passed down so the chrome inside renders
  // the same active entry the width was computed from.
  const [active, setActive] = useRailSection();
  const { canSee } = usePermissions();
  const expanded = !collapsed && hasPanelItems(active, (i) => !i.resource || canSee(i.resource));
  return (
    <aside
      className={cn(
        // No border-r: sidebar shares the page background, like Linear.
        // Visual separation comes from the small inset of nav items and
        // the topbar / page padding alone.
        "hidden md:flex flex-col bg-background transition-[width] duration-150",
        expanded ? "w-sidebar" : "w-14",
      )}
    >
      <SidebarNav collapsed={collapsed} active={active} setActive={setActive} />
    </aside>
  );
}
